import type { ErrorLogPublic, NivelError } from '@app/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';

import { Alert } from '../../../components/ui/alert';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { FormField } from '../../../components/ui/form-field';
import { Textarea } from '../../../components/ui/textarea';
import { useI18n } from '../../../i18n/I18nProvider';
import { getErrorMessage } from '../../../lib/api';
import { qk } from '../../../lib/queryKeys';
import { erroresApi } from '../api/hardening.api';

type FiltroEstado = 'todos' | 'noResueltos' | 'resueltos';

export function ErroresAdminPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [filtro, setFiltro] = useState<FiltroEstado>('noResueltos');
  const [detalle, setDetalle] = useState<ErrorLogPublic | null>(null);
  const es = useI18n();

  const queryParams: Record<string, unknown> = {};
  if (filtro === 'noResueltos') queryParams.resuelto = false;
  if (filtro === 'resueltos') queryParams.resuelto = true;

  const { data, isLoading, error } = useQuery({
    queryKey: qk.admin.errores(queryParams),
    queryFn: () => erroresApi.list(queryParams),
  });

  const mutationResolver = useMutation({
    mutationFn: (vars: { id: string; resuelto: boolean; notas?: string }) =>
      erroresApi.resolver(vars.id, { resuelto: vars.resuelto, ...(vars.notas ? { notas: vars.notas } : {}) }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'errores'] });
      toast.success(es.erroresAdmin.exitoResolver);
      setDetalle(null);
    },
    onError: (e) => toast.error(getErrorMessage(e, es.errores.generico.algoSalioMal)),
  });

  if (isLoading) return <p className="p-6 text-sm text-marca-gris/70">{es.erroresAdmin.cargando}</p>;
  if (error) {
    return (
      <div className="p-6">
        <Alert variant="error">{getErrorMessage(error, es.errores.generico.algoSalioMal)}</Alert>
      </div>
    );
  }

  const errores = data?.errores ?? [];

  return (
    <div className="mx-auto max-w-6xl p-6">
      <h1 className="font-titulos text-3xl font-bold text-text-marca">{es.erroresAdmin.titulo}</h1>
      <p className="mt-1 text-sm text-marca-gris/80">{es.erroresAdmin.descripcion}</p>

      <div className="mt-4 flex gap-2 text-xs">
        {(['noResueltos', 'todos', 'resueltos'] as const).map((f) => (
          <button
            key={f}
            type="button"
            className={
              filtro === f
                ? 'rounded bg-marca-primario-oscuro px-3 py-1 text-white'
                : 'rounded border border-marca-neutro px-3 py-1 text-marca-gris hover:bg-marca-neutro/60'
            }
            onClick={() => setFiltro(f)}
          >
            {es.erroresAdmin.filtros[f]}
          </button>
        ))}
      </div>

      <div className="mt-4 overflow-x-auto rounded-md border border-marca-neutro bg-surface">
        <table className="w-full text-xs">
          <thead className="border-b border-marca-neutro bg-marca-neutro/30 text-[10px] uppercase text-marca-gris/70">
            <tr>
              <th className="px-2 py-2 text-left">{es.erroresAdmin.col.fecha}</th>
              <th className="px-2 py-2 text-left">{es.erroresAdmin.col.nivel}</th>
              <th className="px-2 py-2 text-left">{es.erroresAdmin.col.mensaje}</th>
              <th className="px-2 py-2 text-left">{es.erroresAdmin.col.path}</th>
              <th className="px-2 py-2 text-center">{es.erroresAdmin.col.estado}</th>
              <th className="px-2 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {errores.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-2 py-6 text-center text-marca-gris/60">
                  {es.erroresAdmin.vacio}
                </td>
              </tr>
            ) : (
              errores.map((e) => (
                <tr key={e.id} className="border-b border-marca-neutro/40">
                  <td className="px-2 py-1 text-marca-gris/70">
                    {new Date(e.creadoEn).toLocaleString('es-AR', {
                      timeZone: 'America/Argentina/Buenos_Aires',
                    })}
                  </td>
                  <td className="px-2 py-1">
                    <NivelBadge nivel={e.nivel} />
                  </td>
                  <td className="max-w-[280px] truncate px-2 py-1" title={e.mensaje}>
                    {e.mensaje}
                  </td>
                  <td className="px-2 py-1 font-mono text-[10px] text-marca-gris/70">
                    {e.requestMethod} {e.requestPath}
                  </td>
                  <td className="px-2 py-1 text-center">
                    {e.resuelto ? (
                      <span className="rounded bg-success/20 px-2 py-0.5 text-[10px] text-success">
                        {es.erroresAdmin.estado.resuelto}
                      </span>
                    ) : (
                      <span className="rounded bg-warning/20 px-2 py-0.5 text-[10px] text-warning">
                        {es.erroresAdmin.estado.pendiente}
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-1 text-right">
                    <Button size="sm" variant="outline" onClick={() => setDetalle(e)}>
                      {es.erroresAdmin.detalle}
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {detalle && (
        <ErrorDetalleDialog
          error={detalle}
          open
          onOpenChange={(o) => !o && setDetalle(null)}
          onResolver={(resuelto, notas) =>
            mutationResolver.mutate({ id: detalle.id, resuelto, ...(notas ? { notas } : {}) })
          }
          isPending={mutationResolver.isPending}
        />
      )}
    </div>
  );
}

function NivelBadge({ nivel }: { nivel: NivelError }): JSX.Element {
  const color =
    nivel === 'FATAL'
      ? 'bg-danger text-white'
      : nivel === 'ERROR'
        ? 'bg-warning/30 text-warning'
        : 'bg-info-light/40 text-info';
  return <span className={`rounded px-2 py-0.5 text-[10px] font-semibold ${color}`}>{nivel}</span>;
}

function ErrorDetalleDialog({
  error,
  open,
  onOpenChange,
  onResolver,
  isPending,
}: {
  error: ErrorLogPublic;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onResolver: (resuelto: boolean, notas?: string) => void;
  isPending: boolean;
}): JSX.Element {
  const [notas, setNotas] = useState(error.notas ?? '');
  const es = useI18n();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-mono text-sm">
            [{error.nivel}] {error.mensaje}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3 text-xs">
          <div className="grid grid-cols-2 gap-2 text-marca-gris/80">
            <div>
              <span className="font-semibold">Path: </span>
              <code>
                {error.requestMethod} {error.requestPath}
              </code>
            </div>
            <div>
              <span className="font-semibold">Correlation ID: </span>
              <code>{error.correlationId}</code>
            </div>
            <div className="col-span-2">
              <span className="font-semibold">Fecha: </span>
              {new Date(error.creadoEn).toLocaleString('es-AR', {
                timeZone: 'America/Argentina/Buenos_Aires',
              })}
            </div>
          </div>

          {error.stack && (
            <div>
              <h3 className="mb-1 text-[10px] font-semibold uppercase text-marca-gris/70">
                {es.erroresAdmin.stack}
              </h3>
              <pre className="max-h-64 overflow-auto rounded bg-marca-neutro/40 p-2 font-mono text-[10px]">
                {error.stack}
              </pre>
            </div>
          )}

          {error.requestBody != null && (
            <div>
              <h3 className="mb-1 text-[10px] font-semibold uppercase text-marca-gris/70">
                {es.erroresAdmin.request}
              </h3>
              <pre className="max-h-32 overflow-auto rounded bg-marca-neutro/40 p-2 font-mono text-[10px]">
                {JSON.stringify(error.requestBody, null, 2)}
              </pre>
            </div>
          )}

          <FormField label={es.erroresAdmin.notas} htmlFor="err-notas">
            <Textarea
              id="err-notas"
              rows={2}
              placeholder={es.erroresAdmin.placeholderNotas}
              value={notas}
              onChange={(ev) => setNotas(ev.target.value)}
            />
          </FormField>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {es.erroresAdmin.cancelar}
          </Button>
          {error.resuelto ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => onResolver(false, notas)}
              disabled={isPending}
            >
              {es.erroresAdmin.marcarPendiente}
            </Button>
          ) : (
            <Button type="button" onClick={() => onResolver(true, notas)} disabled={isPending}>
              {es.erroresAdmin.marcarResuelto}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
